import { Button } from '@app/components';
import { PricingPlans } from '@app/components/PricingPlans';
import Logo from '@app/assets/vertical-logo.svg?react';
import { useWelcomePage } from './Welcome.hooks';
import { useQuery } from '@tanstack/react-query';
import { getPlans } from '@app/services/plans';
import { WELCOME_FEATURES } from '@app/utils/consts';
import { FeatureInterface, PlanInterface } from '@app/models';

export const Welcome = () => {
  const {
    navigate,
    handleStartPlan,
    isCreatingAccount,
    selectedPlan,
    setSelectedPlan,
    activeTab,
    setActiveTab,
    selectedPlanType,
    setSelectedPlanType
  } = useWelcomePage();

  // Fetch plans from API
  const { data: plansResponse } = useQuery({
    queryKey: ['get-plans'],
    queryFn: () => getPlans(),
  });

  // Get selected plan data from plansResponse
  const selectedPlanData = plansResponse?.data?.plans?.find((plan: PlanInterface) => plan.type === selectedPlanType);

  const renderProgressBar = () => {
    return (
      <div className="flex gap-2 mb-6">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className={`h-1 flex-1 rounded ${index < 5 ? (index === 4 ? 'bg-[#3279C5]' : 'bg-brand-baseBlue') : 'bg-gray-200'}`} />
        ))}
      </div>
    );
  };

  return (
    <div className="flex items-center justify-center flex-col min-h-screen py-6">
      <Logo width={139} height={110} className="mb-4" />
      <div className="bg-white text-center space-y-6 rounded max-w-[1200px] px-10 pt-10 pb-6 mb-auto">
        {renderProgressBar()}

        <h2 className="font-bold text-3xl">Try Echo Ratings Risk Free</h2>
        <p className="text-base-coolGray max-w-2xl mx-auto">
          Explore Echo Ratings for 14 days, risk free. No credit card required.
          Access features on the Pro plan until <strong>14 days</strong>.
          Upgrade at anytime.
        </p>

        {/* Tab Navigation */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => setActiveTab('plan')}
            className={`relative px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'plan' ? 'bg-brand-baseBlue text-white border border-brand-baseBlue' : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            Start with a Plan
            {activeTab === 'plan' && <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">Save 10%</span>}
          </button>
          <button
            onClick={() => setActiveTab('free')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'free' ? 'bg-brand-baseBlue text-white border border-brand-baseBlue' : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            Try for Free (No Card Needed)
          </button>
        </div>

        {activeTab === 'plan' ? (
          <>
            <p className="text-base-coolGray mb-8">Get 14 days free + 10% off when you choose a plan now and add billing info.</p>

            <PricingPlans
              selectedPlan={selectedPlan}
              onPlanSelect={setSelectedPlan}
              showTrialOffer={true}
              plans={plansResponse?.data?.plans}
              onPlanTypeSelect={setSelectedPlanType}
              buttonText={'Select Plan'}
            />
          </>
        ) : (
          <>
            <div className="text-center mb-12">
              <h3 className="text-xl font-semibold mb-4">Start Your Free Trial</h3>
              <p className="text-base-coolGray max-w-md mx-auto">No credit card required. Get full access to all features for 14 days, completely free.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mx-auto mb-12 max-w-3xl">
              {plansResponse?.data?.plans
                .find((plan: PlanInterface) => plan.type === 'PROFESSIONAL')
                ?.features?.map((feature: FeatureInterface) => (
                  <div key={feature.id} className="flex items-start gap-3 text-sm text-gray-700 text-left">
                    <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0 mt-1.5"></span>
                    <span className="flex-1">{feature.name}</span>
                  </div>
                ))}
            </div>
          </>
        )}

        {/* Paid Features Section */}
        <div className="text-center mb-8">
          <h3 className="text-xl font-semibold mb-2 text-brand-baseBlue">What's included in every plan</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {WELCOME_FEATURES.map((feature, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="flex justify-center mb-4">{feature.icon}</div>
              <h4 className="font-semibold text-sm mb-2">{feature.title}</h4>
              <p className="text-xs text-base-coolGray">{feature.description}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col items-center gap-4 w-full">
          <Button
            onClick={handleStartPlan}
            disabled={isCreatingAccount}
            loading={isCreatingAccount}
            className="w-full bg-blue-400 hover:bg-blue-500 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
          >
            {
              activeTab === 'plan'
                ? (selectedPlanData?.price > 0 ? `Start for $${selectedPlanData?.price}` : 'Start for free')
                : 'Start 14-Day Free Trial'
            }
          </Button>

          <button
            onClick={() => navigate('/sign-up?step=4')}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <span>←</span>
            Previous Step
          </button>
        </div>
      </div>
      <p className="mt-auto text-base-coolGray">
        Have an account?{' '}
        <button onClick={() => navigate('/login')} className="text-brand-baseBlue hover:underline">
          Log In
        </button>
      </p>
    </div>
  );
};
